import apiClient from '../axios.js';

/**
 * 預約管理 API
 */
export const reservationService = {
  /**
   * 查詢訂位列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 訂位列表
   */
  getReservations: async (params = {}) => {
    const response = await apiClient.get('/reservations', { params });
    return response;
  },

  /**
   * 查詢訂位詳情
   * @param {string} reservationId - 訂位 ID
   * @returns {Promise<Object>} 訂位詳情
   */
  getReservation: async (reservationId) => {
    const response = await apiClient.get(`/reservations/${reservationId}`);
    return response;
  },

  /**
   * 查詢可訂位時段
   * @param {string} storeId - 門市 ID
   * @param {string} date - 日期
   * @param {number} guestCount - 用餐人數
   * @returns {Promise<Object>} 可訂位時段
   */
  getAvailableSlots: async (storeId, date, guestCount, excludeReservationId) => {
    const response = await apiClient.get('/reservations/available-slots', {
      params: {
        storeId,
        date,
        guestCount,
        excludeReservationId: excludeReservationId || undefined
      }
    });
    return response;
  },

  /**
   * 查詢包廂類別清單（客戶可自訂）
   * @returns {Promise<Object>} 包廂類別列表
   */
  getRoomCategories: async () => {
    const response = await apiClient.get('/reservations/room-categories');
    return response;
  },

  /**
   * 建立訂位
   * @param {Object} reservationData - 訂位資料
   * @returns {Promise<Object>} 建立結果
   */
  createReservation: async (reservationData) => {
    const response = await apiClient.post('/reservations', reservationData);
    return response;
  },

  /**
   * 更新訂位
   * @param {string} reservationId - 訂位 ID
   * @param {Object} reservationData - 訂位資料
   * @returns {Promise<Object>} 更新結果
   */
  updateReservation: async (reservationId, reservationData) => {
    const response = await apiClient.put(`/reservations/${reservationId}`, reservationData);
    return response;
  },

  /**
   * 取消訂位（將狀態改為 cancelled）
   * @param {string} reservationId - 訂位 ID
   * @returns {Promise<Object>} 取消結果
   */
  cancelReservation: async (reservationId) => {
    const response = await apiClient.delete(`/reservations/${reservationId}`);
    return response;
  },

  /**
   * 永久刪除訂位記錄
   * @param {string} reservationId - 訂位 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteReservation: async (reservationId) => {
    const response = await apiClient.delete(`/reservations/${reservationId}/permanent`);
    return response;
  },

  /**
   * 更新訂位狀態
   * @param {string} reservationId - 訂位 ID
   * @param {string} status - 新狀態
   * @returns {Promise<Object>} 更新結果
   */
  updateReservationStatus: async (reservationId, status) => {
    const response = await apiClient.put(`/reservations/${reservationId}/status`, { status });
    return response;
  },

  /**
   * 手動同步至 Calendar
   * @param {string} reservationId - 訂位 ID
   * @returns {Promise<Object>} 同步結果
   */
  manualSyncReservation: async (reservationId) => {
    const response = await apiClient.patch(`/reservations/${reservationId}/sync`);
    return response;
  },

  /**
   * 手動同步至 Calendar（向後兼容別名，使用舊的 POST 路由）
   * @param {string} reservationId - 訂位 ID
   * @returns {Promise<Object>} 同步結果
   */
  syncToCalendar: async (reservationId) => {
    const response = await apiClient.post(`/reservations/${reservationId}/sync-calendar`);
    return response;
  },

  /**
   * 查詢座位列表
   * @param {string} storeId - 門市 ID
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 座位列表
   */
  getTables: async (storeId, params = {}) => {
    const response = await apiClient.get(`/reservations/stores/${storeId}/tables`, { params });
    return response;
  },

  /**
   * 建立座位
   * @param {string} storeId - 門市 ID
   * @param {Object} tableData - 座位資料
   * @returns {Promise<Object>} 建立結果
   */
  createTable: async (storeId, tableData) => {
    const response = await apiClient.post(`/reservations/stores/${storeId}/tables`, tableData);
    return response;
  },

  /**
   * 查詢座位詳情
   * @param {string} storeId - 門市 ID
   * @param {string} tableId - 座位 ID
   * @returns {Promise<Object>} 座位詳情
   */
  getTable: async (storeId, tableId) => {
    const response = await apiClient.get(`/reservations/stores/${storeId}/tables/${tableId}`);
    return response;
  },

  /**
   * 更新座位
   * @param {string} tableId - 座位 ID
   * @param {Object} tableData - 座位資料
   * @returns {Promise<Object>} 更新結果
   */
  updateTable: async (tableId, tableData) => {
    const response = await apiClient.put(`/reservations/tables/${tableId}`, tableData);
    return response;
  },

  /**
   * 刪除座位
   * @param {string} tableId - 座位 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteTable: async (tableId) => {
    const response = await apiClient.delete(`/reservations/tables/${tableId}`);
    return response;
  },

  /**
   * 查詢座位使用狀態
   * @param {string} storeId - 門市 ID
   * @param {string} date - 日期
   * @returns {Promise<Object>} 座位使用狀態
   */
  getTableStatus: async (storeId, date) => {
    const response = await apiClient.get(`/reservations/stores/${storeId}/tables/status`, {
      params: { date }
    });
    return response;
  },

  /**
   * 查詢候位列表
   * @param {string} storeId - 門市 ID
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 候位列表
   */
  getWaitlist: async (storeId, params = {}) => {
    const response = await apiClient.get(`/reservations/stores/${storeId}/waitlist`, { params });
    return response;
  },

  /**
   * 登記候位
   * @param {string} storeId - 門市 ID
   * @param {Object} waitlistData - 候位資料
   * @returns {Promise<Object>} 登記結果
   */
  createWaitlist: async (storeId, waitlistData) => {
    const response = await apiClient.post(`/reservations/stores/${storeId}/waitlist`, waitlistData);
    return response;
  },

  /**
   * 更新候位狀態
   * @param {string} waitlistId - 候位 ID
   * @param {string} status - 新狀態
   * @param {Object} updateData - 更新資料
   * @returns {Promise<Object>} 更新結果
   */
  updateWaitlistStatus: async (waitlistId, status, updateData = {}) => {
    const response = await apiClient.put(`/reservations/waitlist/${waitlistId}/status`, {
      status,
      ...updateData
    });
    return response;
  },

  /**
   * 查詢門市店休日列表
   */
  getStoreClosures: async (params = {}) => {
    const response = await apiClient.get('/reservations/store-closures', { params });
    return response;
  },

  /**
   * 查詢日曆區間店休日
   */
  getStoreClosuresForCalendar: async ({ storeId, startDate, endDate }) => {
    const response = await apiClient.get('/reservations/store-closures/calendar', {
      params: { storeId, startDate, endDate }
    });
    return response;
  },

  /**
   * 新增門市店休日
   */
  createStoreClosure: async (data) => {
    const response = await apiClient.post('/reservations/store-closures', data);
    return response;
  },

  /**
   * 更新門市店休日
   */
  updateStoreClosure: async (closureId, data) => {
    const response = await apiClient.put(`/reservations/store-closures/${closureId}`, data);
    return response;
  },

  /**
   * 刪除門市店休日
   */
  deleteStoreClosure: async (closureId) => {
    const response = await apiClient.delete(`/reservations/store-closures/${closureId}`);
    return response;
  }
};
